import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi } from "../api/authApi";
import { identifyUser, resetAnalytics } from "../../../shared/lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [token, setTokenState] = useState(() => localStorage.getItem("token"));

  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: authApi.me,
    enabled: Boolean(token),
    retry: false,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (meQuery.data?.user) {
      identifyUser(meQuery.data.user);
    }
  }, [meQuery.data?.user]);

  const login = useCallback(
    (loginResponse) => {
      localStorage.setItem("token", loginResponse.token);
      setTokenState(loginResponse.token);
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
    [queryClient]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // mesmo se o logout no servidor falhar, a sessão local é encerrada
    }
    localStorage.removeItem("token");
    setTokenState(null);
    resetAnalytics();
    queryClient.clear();
  }, [queryClient]);

  const value = {
    token,
    isAuthenticated: Boolean(token),
    user: meQuery.data?.user,
    perfil: meQuery.data?.perfil,
    isEmailVerified: meQuery.data?.user?.is_email_verified ?? false,
    isLoadingMe: meQuery.isLoading,
    refetchMe: meQuery.refetch,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return ctx;
}
